import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { displayNum } from "../variables/numbersFunctions";
import { frenchDate } from "../variables/functions";
// ─── Helpers ────────────────────────────────────────────────────────────────

const getEtatLabel = (etat) => {
    switch (etat) {
        case "VALIDE":
            return "Validé";
        case "EN_ATTENTE":
            return "En attente";
        case "EN_ATTENTE_D_ENCAISSEMENT":
            return "En attente d'encaissement";
        case "ENCAISSE":
            return "Encaissé";
        case "CHEQUE_ANNULE":
            return "Chèque annulé";
        case "REJETE":
            return "Rejeté";
        default:
            return etat || "";
    }
};

const formattedNow = () =>
    new Date().toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

// ─── PDF ────────────────────────────────────────────────────────────────────

export const generateReglementsPDF = (decaissements, sinistre) => {
    const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
    });

    const numSinistre =
        sinistre?.numSinistre ||
        sinistre?.numeroSinistreM ||
        sinistre?.numeroSinistreP ||
        "";

    const headers = [
        "N° Reg.",
        "Bénéficiaire",
        "Montant TTC (FCFA)",
        "Montant Retenu (FCFA)",
        "Net à Payer (FCFA)",
        "N° Chèque",
        "Banque",
        "Date Règlement",
        "État",
    ];

    let totalTTC = 0;
    let totalRetenu = 0;
    let totalNet = 0;

    const rows = decaissements.map((item) => {
        const montantTTC = item.montantGlobal || 0;
        const taxes = item.taxes || 0;
        const net = montantTTC - taxes;

        totalTTC += montantTTC;
        totalRetenu += taxes;
        totalNet += net;

        const benef = item.beneficiaire
            ? `${item.beneficiaire.nom || ""} ${item.beneficiaire.prenom || ""}`.trim()
            : "";

        return [
            item.numero || "",
            benef,
            displayNum(Math.trunc(montantTTC)),
            displayNum(Math.trunc(taxes)),
            displayNum(Math.trunc(net)),
            item.numeroCheque || "-",
            item.banque || "-",
            item.dateReglement ? frenchDate(item.dateReglement) : "",
            getEtatLabel(item.etatDecaissement),
        ];
    });

    // Ligne de total
    rows.push([
        {
            content: "TOTAUX",
            colSpan: 2,
            styles: { halign: "right", fontStyle: "bold" },
        },
        {
            content: displayNum(Math.trunc(totalTTC)),
            styles: { fontStyle: "bold" },
        },
        {
            content: displayNum(Math.trunc(totalRetenu)),
            styles: { fontStyle: "bold" },
        },
        {
            content: displayNum(Math.trunc(totalNet)),
            styles: { fontStyle: "bold" },
        },
        "",
        "",
        "",
        "",
    ]);

    autoTable(doc, {
        head: [headers],
        body: rows,
        theme: "striped",
        styles: {
            fontSize: 8,
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
        },
        headStyles: { fillColor: [36, 33, 102] }, // #242166
        bodyStyles: {
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
            fillColor: [255, 255, 255],
            textColor: 20,
        },
        margin: { top: 40 },
        columnStyles: {
            0: { cellWidth: 18, halign: "center" },
            1: { cellWidth: 40 },
            2: { cellWidth: 30, halign: "right" },
            3: { cellWidth: 30, halign: "right" },
            4: { cellWidth: 30, halign: "right" },
            5: { cellWidth: 25, halign: "center" },
            6: { cellWidth: 25, halign: "center" },
            7: { cellWidth: 30, halign: "center" },
            8: { cellWidth: 35, halign: "center" },
        },
        didDrawPage: () => {
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const pageCount = doc.internal.getNumberOfPages();
            const currentPage = doc.internal.getCurrentPageInfo().pageNumber;

            // En-tête
            doc.setFontSize(16).setFont("helvetica", "bold").setTextColor(36, 33, 102);
            doc.text("POOL TPVM-VT GIE", 14, 15);

            doc.setFontSize(12).setFont("helvetica", "normal").setTextColor(0, 0, 0);
            doc.text(`Sinistre N° : ${numSinistre}`, pageWidth - 70, 15);

            doc.setFontSize(14).setFont("helvetica", "bold");
            doc.text(
                `ÉTAT DES RÈGLEMENTS - DOSSIER SINISTRE N° ${numSinistre}`,
                pageWidth / 2,
                25,
                { align: "center" }
            );

            doc.setLineWidth(0.5);
            doc.line(14, 30, pageWidth - 14, 30);

            // Pied de page
            doc.setFontSize(10).setFont("helvetica", "normal");
            doc.text(
                `Généré le : ${formattedNow()} par PoolIns`,
                14,
                pageHeight - 10
            );
            doc.setFontSize(9);
            doc.text(
                `Page ${currentPage} / ${pageCount}`,
                pageWidth - 25,
                pageHeight - 10
            );
        },
    });

    // Ouvrir dans un nouvel onglet ou télécharger
    const pdfOutput = doc.output("datauristring");
    const link = document.createElement("a");
    link.href = pdfOutput;
    link.download = `Reglements_Sinistre_${numSinistre}_${formattedNow().replace(/[/:]/g, "-")}.pdf`;
    link.click();
};

// ─── Excel ──────────────────────────────────────────────────────────────────

export const generateReglementsExcel = (decaissements, sinistre) => {
    const numSinistre =
        sinistre?.numSinistre ||
        sinistre?.numeroSinistreM ||
        sinistre?.numeroSinistreP ||
        "";

    const wsData = [];
    let totalTTC = 0;
    let totalRetenu = 0;
    let totalNet = 0;

    // Lignes d'en-tête
    wsData.push(["POOL TPVM-VT GIE"]);
    wsData.push([`ÉTAT DES RÈGLEMENTS - DOSSIER SINISTRE N° ${numSinistre}`]);
    wsData.push([`Généré le : ${formattedNow()}`]);
    wsData.push([]);

    // En-têtes de colonnes
    wsData.push([
        "N° Règlement",
        "Bénéficiaire",
        "Montant TTC (FCFA)",
        "Montant Retenu (FCFA)",
        "Net à Payer (FCFA)",
        "N° Chèque",
        "Banque",
        "Date Règlement",
        "État",
    ]);

    // Données
    decaissements.forEach((item) => {
        const montantTTC = item.montantGlobal || 0;
        const taxes = item.taxes || 0;
        const net = montantTTC - taxes;

        totalTTC += montantTTC;
        totalRetenu += taxes;
        totalNet += net;

        const benef = item.beneficiaire
            ? `${item.beneficiaire.nom || ""} ${item.beneficiaire.prenom || ""}`.trim()
            : "";

        wsData.push([
            item.numero || "",
            benef,
            Math.trunc(montantTTC),
            Math.trunc(taxes),
            Math.trunc(net),
            item.numeroCheque || "-",
            item.banque || "-",
            item.dateReglement ? frenchDate(item.dateReglement) : "",
            getEtatLabel(item.etatDecaissement),
        ]);
    });

    // Ligne de total
    wsData.push([]);
    wsData.push([
        "",
        "TOTAUX",
        Math.trunc(totalTTC),
        Math.trunc(totalRetenu),
        Math.trunc(totalNet),
        "",
        "",
        "",
        "",
    ]);

    // Création du workbook
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Largeurs de colonnes
    ws["!cols"] = [
        { wch: 14 }, // N° Règlement
        { wch: 30 }, // Bénéficiaire
        { wch: 20 }, // Montant TTC
        { wch: 20 }, // Montant Retenu
        { wch: 20 }, // Net à Payer
        { wch: 16 }, // N° Chèque
        { wch: 16 }, // Banque
        { wch: 18 }, // Date
        { wch: 24 }, // État
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Règlements");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
        new Blob([excelBuffer], { type: "application/octet-stream" }),
        `Reglements_Sinistre_${numSinistre}_${formattedNow().replace(/[/:]/g, "-")}.xlsx`
    );
};